import React, {
  useContext, useEffect, useMemo, useState,
} from 'react';
import { Grid } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import RatePlanOption from './RatePlanOption';
import { MonthlyBillEstimate } from '../bill';
import { FeatureContext } from '../../FeatureContext';
import { ContainerFrame } from '../frame';
import {
  BillEstimateResponse, IAccountReduxState, IAppReduxState, VisualizerDetailsMonthData, VisualizerMonthData, VisualizerPlanData,
} from '../../models';
import { BillService, CustomerService, VisualizerService } from '../../services';
import { updateEstimations } from '../../reducers/bill-reducer';
import { updateCustomerDetail } from '../../reducers/account-reducer';
import { setSelectedMeterID } from '../../reducers/user-input-reducer';
import { CustomerDetail } from '../customer';
import { updateVisualizerOverviewData, updateVisualizerDetailsData } from '../../reducers/visualizer-reducer';

function RatePlan() {
  const dispatch = useDispatch();
  const featureContext = useContext(FeatureContext);
  const account = useSelector<IAppReduxState, IAccountReduxState>((state) => state.account);
  const selectedMeterId = useSelector<IAppReduxState, string>((state) => state.userInput.selectedMeterID);
  const billEstimateResp = useSelector<IAppReduxState, BillEstimateResponse[] | undefined>(
    (state) => state.bill.estimations[selectedMeterId],
  );
  const visualizerOverviewData = useSelector<IAppReduxState, VisualizerPlanData<VisualizerMonthData>[] | undefined>(
    (state) => state.visualizer.visualizerOverviewData[selectedMeterId],
  );
  const visualizerDetailsData = useSelector<IAppReduxState, VisualizerPlanData<VisualizerDetailsMonthData>[] | undefined>(
    (state) => state.visualizer.visualizerDetailsData[selectedMeterId],
  );
  const stateCode = useSelector<IAppReduxState, string>((x) => x.account.stateCode);

  const [customerLoading, setCustomerLoading] = useState(false);
  const [billEstimationLoading, setBillEstimationLoading] = useState(true);
  const [visualizerOverviewLoading, setVisualizerOverviewLoading] = useState(true);
  const [visualizerDetailsLoading, setVisualizerDetailsLoading] = useState(true);

  const [customerApiError, setCustomerApiError] = useState('');
  const [billApiError, setBillApiError] = useState('');

  const selectedServicePoint = useMemo(
    () => account?.servicePoints.find((sp) => sp.meterNumber === selectedMeterId),
    [account?.servicePoints, selectedMeterId],
  );

  useEffect(() => {
    if (selectedServicePoint) {
      if (selectedServicePoint.errorCode !== '') {
        window.dataLayer.push({
          event: 'Exception',
          error_text: selectedServicePoint.errorCode,
        });
      }
    }
  }, [selectedServicePoint]);

  useEffect(() => {
    async function getCustomerDetails() {
      setCustomerApiError('');
      try {
        setCustomerLoading(true);
        const response = await CustomerService.getCustomerDetail();
        if (response) {
          dispatch(updateCustomerDetail(response));
          dispatch(setSelectedMeterID(response.servicePoints[0].meterNumber));
          setCustomerLoading(false);
        }
      } catch (error: any) {
        if (error.response) {
          // Exception recorded twice
          window.dataLayer.push({
            event: 'Exception',
            error_text: error.response.data.message,
          });
          setCustomerApiError(error.response.data.message);
          setCustomerLoading(false);
          setBillEstimationLoading(false);
        }
      }
    }
    if (!selectedServicePoint && !customerLoading && !customerApiError) {
      getCustomerDetails();
    }
  }, [customerApiError, customerLoading, dispatch, selectedServicePoint]);

  useEffect(() => {
    async function getBillEstimation() {
      try {
        if (billEstimateResp) {
          setBillEstimationLoading(false);
          return;
        }

        const servicePoint = account.servicePoints.find((x) => x.meterNumber === selectedMeterId);
        if (servicePoint) {
          setBillEstimationLoading(true);
          if (selectedServicePoint) {
            const resp = await BillService.getMonthlyEstimatedBill(
              servicePoint.currentPlanCode,
              servicePoint?.servicePointId ?? '',
              featureContext.FEB_2025_IA_RATES,
            );
            dispatch(updateEstimations({ selectedMeterId, estimations: resp }));
            let infoText = resp.map((x) => `Plan ${
              x.planCode
            }; Total Price $: ${
              x.totalPlanCharges
            }; Estimated Savings $: ${
              x.estimatedSavings
            }`).join(' | ');
            // Log in analytics if customer is already on best rate plan
            // i.e. estimated savings is less than equal to $0 for every rate plan
            if (resp.map((x) => x.estimatedSavings).every((y) => y <= 0)) {
              infoText = `Customer is already on best rate plan | ${infoText}`;
            }
            window.dataLayer.push({
              event: 'Info',
              info_text: infoText,
            });
          }
          setBillEstimationLoading(false);
        }
      } catch (err: any) {
        const errCode = err.response.data.message;
        setBillApiError(errCode);
        window.dataLayer.push({
          event: 'Exception',
          error_text: errCode,
        });
        setBillEstimationLoading(false);
      }
    }
    setBillApiError('');
    getBillEstimation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMeterId, billEstimateResp]);

  useEffect(() => {
    async function getVisualizerData() {
      if (visualizerOverviewData) {
        setVisualizerOverviewLoading(false);
        return;
      }
      try {
        if (selectedServicePoint) {
          setVisualizerOverviewLoading(true);
          const resp = await VisualizerService.getVisualizerOverviewData(
            selectedServicePoint.servicePointId ?? '',
            featureContext.ENERGY_USAGE_VISUALIZER ?? true,
            featureContext.ENERGY_USAGE_VISUALIZER_BILLING_CYCLE,
          );
          dispatch(updateVisualizerOverviewData({ selectedMeterId, visualizerOverviewData: resp }));
        }
      } catch (error: any) {
        if (error.response) {
          // Exception recorded twice
          window.dataLayer.push({
            event: 'Exception',
            error_text: error.response.data.message,
          });
        }
      } finally {
        setVisualizerOverviewLoading(false);
      }
    }
    getVisualizerData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    featureContext.ENERGY_USAGE_VISUALIZER,
    featureContext.ENERGY_USAGE_VISUALIZER_BILLING_CYCLE,
    selectedServicePoint?.servicePointId,
    visualizerOverviewData,
  ]);

  useEffect(() => {
    async function getVisualizerDetailsData() {
      if (visualizerDetailsData) {
        setVisualizerDetailsLoading(false);
        return;
      }
      try {
        if (selectedServicePoint) {
          setVisualizerDetailsLoading(true);
          const detailsResp = await VisualizerService.getVisualizerDetailsData(
            selectedServicePoint.servicePointId ?? '',
            featureContext.ENERGY_USAGE_VISUALIZER ?? true,
            featureContext.ENERGY_USAGE_VISUALIZER_BILLING_CYCLE,
          );
          dispatch(updateVisualizerDetailsData({ selectedMeterId, visualizerDetailsData: detailsResp }));
        }
      } catch (error: any) {
        if (error.response) {
          // Exception recorded twice
          window.dataLayer.push({
            event: 'Exception',
            error_text: error.response.data.message,
          });
        }
      } finally {
        setVisualizerDetailsLoading(false);
      }
    }
    getVisualizerDetailsData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    featureContext.ENERGY_USAGE_VISUALIZER,
    featureContext.ENERGY_USAGE_VISUALIZER_BILLING_CYCLE,
    selectedServicePoint?.servicePointId,
    visualizerDetailsData,
  ]);

  const errorCode = useMemo(
    () => (selectedServicePoint?.errorCode || customerApiError || billApiError),
    [billApiError, customerApiError, selectedServicePoint],
  );

  const showComponent = useMemo(() => VisualizerService.shouldShowComponent(errorCode), [errorCode]);

  return (
    <Grid sx={{ backgroundColor: 'mainBackground.main' }}>
      <CustomerDetail isLoading={customerLoading} />
      <RatePlanOption
        billEstimateResponse={billEstimateResp}
        selectedServicePoint={selectedServicePoint}
        isLoading={billEstimationLoading}
        errorCode={errorCode}
      />
      <ContainerFrame>
        {showComponent && billEstimateResp && (
          <MonthlyBillEstimate
            selectedServicePoint={selectedServicePoint}
            billEstimateResponse={billEstimateResp}
            visualizerOverviewData={visualizerOverviewData}
            visualizerDetailsData={visualizerDetailsData}
            visualizerOverviewLoading={visualizerOverviewLoading}
            visualizerDetailsLoading={visualizerDetailsLoading}
            stateCode={stateCode}
          />
        )}
      </ContainerFrame>
    </Grid>
  );
}

export { RatePlan };
